import { useEffect, useRef, useState } from "react";
import { GripVertical, Moon, Sun } from "lucide-react";
import { UI_REVAMP_ENABLED } from "../config/featureFlags";

// UI revamp: the sidebars' own switch (SidebarThemeToggle) asks this component
// to flip the mode, so the state and its localStorage stay in one place.
export const TOGGLE_LIGHT_MODE_EVENT = "regx:toggle-light-mode";

const STORAGE_KEY = "regxLightModeToggle";
// Bumped to 3 when light mode became the app-wide default. The bump invalidates
// the old auto-persisted `enabled: false` so existing users pick up the new
// light default; anyone who explicitly toggles dark is remembered under v3.
const STORAGE_VERSION = 3;
const DEFAULT_OFFSET = { left: 18, bottom: 22 };

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getDefaultPosition = () => {
  if (typeof window === "undefined") return { x: DEFAULT_OFFSET.left, y: 88 };
  return {
    x: DEFAULT_OFFSET.left,
    y: Math.max(8, window.innerHeight - 52 - DEFAULT_OFFSET.bottom),
  };
};

const loadStoredState = () => {
  // Light mode is the default; dark is only used when the user has explicitly
  // toggled it under the current storage version.
  if (typeof window === "undefined") {
    return { enabled: true, position: getDefaultPosition() };
  }

  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    const hasCurrentVersion = stored.version === STORAGE_VERSION;
    return {
      enabled: hasCurrentVersion ? !!stored.enabled : true,
      position: hasCurrentVersion &&
        stored.position &&
        Number.isFinite(stored.position.x) &&
        Number.isFinite(stored.position.y)
          ? stored.position
          : getDefaultPosition(),
    };
  } catch {
    return { enabled: true, position: getDefaultPosition() };
  }
};

// `hidden`: keep the mode working (class + storage) but don't draw the floating
// pill, on pages whose sidebar has the switch instead (UI revamp only).
export default function LightModeToggle({ hidden = false }) {
  const initial = useRef(loadStoredState());
  const [enabled, setEnabled] = useState(initial.current.enabled);
  const [position, setPosition] = useState(initial.current.position);
  const dragRef = useRef(null);

  useEffect(() => {
    document.documentElement.classList.toggle("light-bg-mode", enabled);
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ enabled, position, version: STORAGE_VERSION })
      );
    } catch {
      // localStorage can be unavailable in restricted browser contexts.
    }
  }, [enabled, position]);

  useEffect(() => {
    const handleResize = () => {
      setPosition((pos) => ({
        x: clamp(pos.x, 8, Math.max(8, window.innerWidth - 72)),
        y: clamp(pos.y, 8, Math.max(8, window.innerHeight - 52)),
      }));
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!UI_REVAMP_ENABLED) return undefined;
    const flip = () => setEnabled((value) => !value);
    window.addEventListener(TOGGLE_LIGHT_MODE_EVENT, flip);
    return () => window.removeEventListener(TOGGLE_LIGHT_MODE_EVENT, flip);
  }, []);

  const handlePointerDown = (event) => {
    event.currentTarget.setPointerCapture?.(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: position.x,
      originY: position.y,
      moved: false,
    };
  };

  const handlePointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) drag.moved = true;

    setPosition({
      x: clamp(drag.originX + dx, 8, Math.max(8, window.innerWidth - 72)),
      y: clamp(drag.originY + dy, 8, Math.max(8, window.innerHeight - 52)),
    });
  };

  const handlePointerUp = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    event.currentTarget.releasePointerCapture?.(event.pointerId);
    dragRef.current = null;

    if (!drag.moved) {
      setEnabled((value) => !value);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    setEnabled((value) => !value);
  };

  if (hidden) return null;

  return (
    <button
      type="button"
      aria-label={enabled ? "Disable background light mode" : "Enable background light mode"}
      aria-pressed={enabled}
      title={enabled ? "Disable background light mode" : "Enable background light mode"}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={() => {
        dragRef.current = null;
      }}
      onKeyDown={handleKeyDown}
      className={`fixed select-none touch-none inline-flex items-center gap-1.5 rounded-full border px-2.5 py-2 shadow-[0_12px_36px_-12px_rgba(0,0,0,0.65)] backdrop-blur-2xl transition-all active:scale-95 ${
        enabled
          ? "bg-amber-100/90 border-amber-300/70 text-slate-900"
          : "bg-slate-950/75 border-white/15 text-slate-100"
      }`}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        zIndex: 9998,
      }}
    >
      <GripVertical className="w-3.5 h-3.5 opacity-55" aria-hidden="true" />
      {enabled ? (
        <Sun className="w-4 h-4 text-amber-500" aria-hidden="true" />
      ) : (
        <Moon className="w-4 h-4 text-slate-300" aria-hidden="true" />
      )}
    </button>
  );
}
